import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEventImage, getPremiumEventImage } from '../utils/eventImages';

const FeaturedEvent = ({ event }) => {
  const [image, setImage] = useState('');
  
  useEffect(() => {
    if (event) {
      const imageUrl = event.image_url || event.image;
      
      if (imageUrl) {
        // Convert HTTP to HTTPS to avoid mixed content issues
        const secureUrl = imageUrl.replace(/^http:\/\//i, 'https://');
        setImage(secureUrl);
      } else {
        const premiumImage = getPremiumEventImage(event.title);
        const finalImage = premiumImage || getEventImage(event.title);
        setImage(finalImage);
      }
    }
  }, [event]);
  
  if (!event) {
    return null;
  }

  const formatDate = (dateString) => {
    try {
      // Parse date string as local time to avoid timezone shift
      let date;
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse date string as local time to avoid timezone shift
    let eventDate;
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      eventDate = new Date(year, month - 1, day);
    } else {
      eventDate = new Date(dateString);
    }
    eventDate.setHours(0, 0, 0, 0);
    
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Event in progress';
    if (diffDays === 0) return 'Today!';
    if (diffDays === 1) return '1 day to go!';
    return `${diffDays} days to go!`;
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Featured Event
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
            Don't miss our next major gathering
          </p>
        </div>
        
        <div className="bg-white overflow-hidden shadow-xl rounded-lg">
          <div className="md:flex">
            <div className="md:flex-shrink-0">
              <img 
                className="h-64 w-full object-cover md:w-96" 
                src={image} 
                alt={event.title}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getEventImage(event.title);
                }}
              />
            </div>
            <div className="p-8">
              <div className="uppercase tracking-wide text-sm text-indigo-600 font-semibold">
                {event.org} • {event.event}
              </div>
              <h3 className="mt-2 text-2xl font-extrabold text-gray-900">
                {event.title}
              </h3>
              <div className="mt-3 flex items-center text-sm text-gray-500">
                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {event.venue}, {event.city}
              </div>
              <div className="mt-1 flex items-center text-sm text-gray-500">
                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                {formatDate(event.start)}{event.end && ` - ${formatDate(event.end)}`}
              </div>
              <p className="mt-3 text-base text-gray-500">
                {event.description?.substring(0, 200)}...
              </p>
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                      {getDaysUntil(event.start)}
                    </span>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <Link 
                    to={`/events/${event.id}`}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedEvent;
